import { useState } from 'react'
import { createContact, updateContact, findContactByEmail } from '../firebase/contacts'
import './BulkImportModal.css'

const BulkImportModal = ({ onClose, userId, existingContacts = [] }) => {
  const [inputText, setInputText] = useState('')
  const [parsedContacts, setParsedContacts] = useState([])
  const [processing, setProcessing] = useState(false)
  const [results, setResults] = useState(null)

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

  // Parse input text into contact objects
  const parseInput = (text) => {
    if (!text.trim()) return []

    const lines = text.split('\n').map(line => line.trim()).filter(line => line)
    const contacts = []

    lines.forEach((line, index) => {
      let name = ''
      let email = ''

      // Try different formats
      // Format 1: "Name <email@example.com>"
      const nameEmailMatch = line.match(/^(.+?)\s*<(.+?)>$/i)
      if (nameEmailMatch) {
        name = nameEmailMatch[1].trim()
        email = nameEmailMatch[2].trim().toLowerCase()
      }
      // Format 2: "email@example.com" or "name,email@example.com"
      else if (line.includes(',')) {
        const parts = line.split(',').map(p => p.trim())
        if (parts.length >= 2) {
          // Check if first part is email
          if (emailRegex.test(parts[0])) {
            email = parts[0].toLowerCase()
            name = parts.slice(1).join(' ').trim()
          } else {
            name = parts[0]
            email = parts[1].toLowerCase()
          }
        }
      }
      // Format 3: Just an email address
      else if (emailRegex.test(line)) {
        email = line.toLowerCase()
        // Try to extract name from email (before @)
        const emailName = email.split('@')[0]
        name = emailName.split(/[._-]/).map(part => 
          part.charAt(0).toUpperCase() + part.slice(1)
        ).join(' ')
      }
      // Format 4: Just a name (no email)
      else {
        name = line
      }

      if (name || email) {
        contacts.push({
          name: name || '',
          email: email || '',
          originalLine: line,
          lineNumber: index + 1
        })
      }
    })

    return contacts
  }

  const handlePreview = () => {
    const parsed = parseInput(inputText)
    setParsedContacts(parsed)
  }

  const handleImport = async () => {
    if (parsedContacts.length === 0) {
      alert('No contacts to import. Please paste some names or emails first.')
      return
    }

    setProcessing(true)
    const importResults = {
      created: 0,
      merged: 0,
      skipped: 0,
      errors: []
    }

    try {
      for (const contact of parsedContacts) {
        try {
          // Skip if no email and no name
          if (!contact.email && !contact.name) {
            importResults.skipped++
            continue
          }

          // If there's an email, check if contact exists
          if (contact.email) {
            const existing = await findContactByEmail(contact.email, userId)
            
            if (existing) {
              // Merge: update existing contact with new name if provided and existing name is empty
              const updates = {}
              if (contact.name && !existing.name) {
                updates.name = contact.name
              }
              
              if (Object.keys(updates).length > 0) {
                await updateContact(existing.id, updates)
                importResults.merged++
              } else {
                importResults.skipped++
              }
            } else {
              // Create new contact
              await createContact({
                name: contact.name || '',
                email: contact.email
              }, userId)
              importResults.created++
            }
          } else {
            // No email, just name - check if name already exists
            const nameExists = existingContacts.some(
              c => c.name?.toLowerCase() === contact.name.toLowerCase()
            )
            
            if (!nameExists) {
              await createContact({
                name: contact.name
              }, userId)
              importResults.created++
            } else {
              importResults.skipped++
            }
          }
        } catch (error) {
          console.error(`Error processing contact: ${contact.originalLine}`, error)
          importResults.errors.push({
            contact: contact.originalLine,
            error: error.message
          })
        }
      }

      setResults(importResults)
    } catch (error) {
      console.error('Error during bulk import:', error)
      alert('An error occurred during import. Please try again.')
    } finally {
      setProcessing(false)
    }
  }

  const handleClose = () => {
    if (results) {
      // Reset for new import
      setInputText('')
      setParsedContacts([])
      setResults(null)
    }
    onClose()
  }

  return (
    <div className="bulk-import-overlay">
      <div className="bulk-import-modal">
        <div className="bulk-import-header">
          <h3>Bulk Import Contacts</h3>
          <button className="close-btn" onClick={handleClose}>×</button>
        </div>

        <div className="bulk-import-content">
          {!results ? (
            <>
              <div className="bulk-import-instructions">
                <p>Paste a list of names or email addresses (one per line). Supported formats:</p>
                <ul>
                  <li>Email addresses: <code>john@example.com</code></li>
                  <li>Names: <code>John Doe</code></li>
                  <li>Name and email: <code>John Doe &lt;john@example.com&gt;</code></li>
                  <li>Comma-separated: <code>John Doe, john@example.com</code></li>
                </ul>
                <p className="note">
                  <strong>Note:</strong> Contacts with matching emails will be merged. 
                  New contacts will be created for emails that don't exist.
                </p>
              </div>

              <div className="form-group">
                <label htmlFor="bulk-input">Paste contacts here:</label>
                <textarea
                  id="bulk-input"
                  className="bulk-input"
                  value={inputText}
                  onChange={(e) => setInputText(e.target.value)}
                  placeholder="john@example.com&#10;Jane Smith &lt;jane@example.com&gt;&#10;Bob Johnson, bob@example.com&#10;Alice Williams"
                  rows={10}
                />
              </div>

              {parsedContacts.length > 0 && (
                <div className="preview-section">
                  <h4>Preview ({parsedContacts.length} contacts):</h4>
                  <div className="preview-list">
                    {parsedContacts.slice(0, 10).map((contact, idx) => (
                      <div key={idx} className="preview-item">
                        <span className="preview-name">{contact.name || '(No name)'}</span>
                        {contact.email && (
                          <span className="preview-email">{contact.email}</span>
                        )}
                      </div>
                    ))}
                    {parsedContacts.length > 10 && (
                      <div className="preview-more">
                        ... and {parsedContacts.length - 10} more
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="bulk-import-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handlePreview}
                  disabled={!inputText.trim()}
                >
                  Preview
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleClose}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleImport}
                  disabled={parsedContacts.length === 0 || processing}
                >
                  {processing ? 'Importing...' : 'Import Contacts'}
                </button>
              </div>
            </>
          ) : (
            <div className="import-results">
              <h4>Import Complete!</h4>
              <div className="results-stats">
                <div className="result-stat success">
                  <span className="stat-label">Created:</span>
                  <span className="stat-value">{results.created}</span>
                </div>
                <div className="result-stat merged">
                  <span className="stat-label">Merged:</span>
                  <span className="stat-value">{results.merged}</span>
                </div>
                <div className="result-stat skipped">
                  <span className="stat-label">Skipped:</span>
                  <span className="stat-value">{results.skipped}</span>
                </div>
                {results.errors.length > 0 && (
                  <div className="result-stat error">
                    <span className="stat-label">Errors:</span>
                    <span className="stat-value">{results.errors.length}</span>
                  </div>
                )}
              </div>

              {results.errors.length > 0 && (
                <div className="errors-list">
                  <h5>Errors:</h5>
                  <ul>
                    {results.errors.map((err, idx) => (
                      <li key={idx}>
                        <strong>{err.contact}:</strong> {err.error}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="bulk-import-actions">
                <button
                  type="button"
                  className="btn-primary"
                  onClick={handleClose}
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default BulkImportModal

